/**
 * GameModal Component
 *
 * Modal for displaying game states: victory, defeat, waiting.
 * Features:
 * - Overlay/backdrop with click-to-close
 * - Keyboard accessibility (Escape to close, focus trap)
 * - Customizable content and actions
 *
 * Props:
 * - isOpen: Whether modal is visible
 * - type: Modal type (victory, defeat, waiting)
 * - title: Custom title (optional)
 * - message: Custom message (optional)
 * - onClose: Callback when modal closed
 * - onPlayAgain: Callback for play again action
 * - children: Additional custom content
 */

import { useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { COPY } from '../copy';

export default function GameModal({
  isOpen = false,
  type,
  title,
  message,
  onClose,
  onPlayAgain,
  closable = true,
  actions = [],
  children,
}) {
  // Modal configurations
  const modalConfig = {
    victory: {
      title: COPY.modal.victory.titleWin,
      message: COPY.modal.victory.messageWin,
      bgColor: 'bg-dark-charcoal',
      borderColor: 'border-neon-green',
      shadowColor: 'shadow-glow-green',
      textColor: 'text-neon-green',
      icon: COPY.phase.gameOver.emojiWin,
      showActions: true,
    },
    defeat: {
      title: COPY.modal.victory.titleLose,
      message: COPY.modal.victory.messageLose,
      bgColor: 'bg-dark-charcoal',
      borderColor: 'border-neon-pink',
      shadowColor: 'shadow-glow-pink',
      textColor: 'text-neon-pink',
      icon: COPY.phase.gameOver.emojiLose,
      showActions: true,
    },
    waiting: {
      title: COPY.phase.waiting.title,
      message: COPY.modal.waiting.waitingToReady,
      bgColor: 'bg-dark-charcoal',
      borderColor: 'border-neon-yellow',
      shadowColor: 'shadow-glow-yellow',
      textColor: 'text-neon-yellow',
      icon: COPY.phase.waiting.emoji,
      showActions: false,
    },
  };

  const config = modalConfig[type] || modalConfig.waiting;
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;

  // Handle Escape key
  useEffect(() => {
    if (!isOpen || !closable) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closable]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Handle overlay click
  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && onClose && closable) {
      onClose();
    }
  };

  const ariaLabel = `${displayTitle} ${displayMessage}`;

  const modalId = `modal-title-${type}`;

  return (
    <div
      data-testid="modal-backdrop"
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 transition-opacity duration-300"
      aria-hidden="false"
    >
      <div
        data-testid="game-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalId}
        className={`${config.bgColor} ${config.textColor} border-2 ${config.borderColor} ${config.shadowColor} rounded-lg p-8 max-w-md w-full mx-4 transition-all duration-300 transform flex flex-col items-center justify-center`}
      >
        {/* Close button */}
        {onClose && closable && (
          <button
            type="button"
            onClick={onClose}
            aria-label={COPY.accessibility.closeButton}
            className="float-right text-2xl hover:opacity-70 transition-opacity"
          >
            ×
          </button>
        )}

        {/* Icon and Title */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4" aria-hidden="true">
            {config.icon}
          </div>
          <h2
            id={modalId}
            className={`text-3xl font-bold mb-2 ${config.textColor}`}
          >
            {displayTitle}
          </h2>
          <p className="text-lg text-gray-300">{displayMessage}</p>
        </div>

        {/* Loading spinner for waiting state */}
        {type === 'waiting' && (
          <div
            className="flex justify-center mb-6 animate-pulse"
            data-testid="modal-spinner-wrapper"
          >
            <LoadingSpinner color="white" size="large" />
          </div>
        )}

        {/* Custom children */}
        {children && <div className="mb-6">{children}</div>}

        {/* Custom action buttons */}
        {actions && actions.length > 0 && (
          <div className="flex flex-col gap-3">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={action.onClick}
                className="bg-neon-cyan border-2 border-neon-cyan text-dark-navy font-bold py-3 px-6 rounded-lg hover:bg-neon-pink hover:border-neon-pink hover:text-white transition-all shadow-glow-cyan"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Default action buttons */}
        {(!actions || actions.length === 0) && config.showActions && (
          <div className="flex flex-col gap-3">
            {onPlayAgain && (
              <button
                type="button"
                onClick={onPlayAgain}
                className="bg-neon-cyan border-2 border-neon-cyan text-dark-navy font-bold py-3 px-6 rounded-lg hover:bg-neon-pink hover:border-neon-pink hover:text-white transition-all shadow-glow-cyan"
              >
                {COPY.modal.victory.buttons.playAgain}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
